<script setup lang="ts">
import { ref, computed } from 'vue'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { getA5VinComparison } from '@/api'

const accountStore = useAccountSettingsStore()
const endpoint = import.meta.env.VITE_ISE_BACKEND_URL

const jazelId = ref(accountStore.accountId)
const vinInput = ref('')
const loading = ref(false)
const error = ref<string | null>(null)
const result = ref<any | null>(null)
const showOnlyDiffs = ref(false)

const selectedA5Ids = ref<number[]>([])
const possibleA5Ids = ref<number[]>([])

const inventoryIds = accountStore.inventoryIds
if (Array.isArray(inventoryIds)) {
  possibleA5Ids.value = inventoryIds
  selectedA5Ids.value = inventoryIds.length ? [inventoryIds[0]] : []
} else if (typeof inventoryIds === 'number') {
  possibleA5Ids.value = [inventoryIds]
  selectedA5Ids.value = [inventoryIds]
}

const filteredComparison = computed(() => {
  const rows = result.value?.comparison ?? []
  if (!showOnlyDiffs.value) return rows
  return rows.filter((r: any) => !r.match)
})

const runComparison = async () => {
  const vin = vinInput.value.trim().toUpperCase()
  if (!vin || !selectedA5Ids.value.length) return

  loading.value = true
  error.value = null
  result.value = null
  showOnlyDiffs.value = false

  try {
    result.value = await getA5VinComparison(endpoint, jazelId.value, vin, {
      a5Ids: selectedA5Ids.value,
    })
  } catch (e: any) {
    console.error(e)
    error.value = e.message || 'Failed to fetch VIN comparison'
  } finally {
    loading.value = false
  }
}

const formatValue = (val: unknown): string => {
  if (val === null || val === undefined) return '—'
  if (Array.isArray(val)) return val.join(', ')
  if (typeof val === 'object') return JSON.stringify(val)
  return String(val)
}
</script>

<template>
  <div class="h-100">
    <v-row align="center">
      <v-col cols="12" md="2">
        <div class="text-caption text-grey">Jazel ID (JUID)</div>
        <div class="text-body-1 font-weight-bold">{{ jazelId }}</div>
      </v-col>
      <v-col cols="12" md="2">
        <div class="text-caption text-grey">A5 Account ID</div>
        <div v-if="possibleA5Ids.length <= 1" class="text-body-1 font-weight-bold">{{ selectedA5Ids[0] ?? '—' }}</div>
        <v-select
          v-else
          v-model="selectedA5Ids"
          :items="possibleA5Ids"
          multiple
          chips
          closable-chips
          variant="underlined"
          density="compact"
          :disabled="loading"
          hide-details
          class="mt-0 pt-0"
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-text-field
          v-model="vinInput"
          label="VIN"
          placeholder="Enter VIN to compare..."
          variant="outlined"
          density="compact"
          hide-details
          clearable
          :disabled="loading || !selectedA5Ids.length"
          @keyup.enter="runComparison"
        />
      </v-col>
      <v-col cols="12" md="2">
        <v-btn
          color="primary"
          :loading="loading"
          :disabled="loading || !vinInput?.trim() || !selectedA5Ids.length"
          @click="runComparison"
        >
          Compare
        </v-btn>
      </v-col>
    </v-row>

    <v-alert v-if="error" type="error" class="mt-4 mb-4">{{ error }}</v-alert>

    <div v-if="loading" class="d-flex justify-center my-8">
      <v-progress-circular indeterminate color="primary" size="48" width="3" />
    </div>

    <div v-else-if="result" class="mt-4">
      <v-alert v-if="!result.a5 && !result.a6" type="warning" class="mb-4">
        VIN not found in either A5 or A6.
      </v-alert>
      <v-alert v-else-if="!result.a5" type="info" variant="tonal" class="mb-4">
        VIN not found in A5 CloudSearch. Only A6 data is shown.
      </v-alert>
      <v-alert v-else-if="!result.a6" type="info" variant="tonal" class="mb-4">
        VIN not found in A6 OpenSearch. Only A5 data is shown.
      </v-alert>

      <!-- Summary cards -->
      <v-row class="mb-4 align-stretch">
        <v-col cols="12" md="3" class="d-flex">
          <v-card variant="tonal" color="info" class="flex-grow-1 d-flex align-center justify-center">
            <v-card-text class="text-center">
              <div class="text-h6">Total Fields</div>
              <div class="text-h4">{{ result.totalFields }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="3" class="d-flex">
          <v-card variant="tonal" :color="result.diffCount === 0 ? 'success' : 'warning'" class="flex-grow-1 d-flex align-center justify-center">
            <v-card-text class="text-center">
              <div class="text-h6">Differences</div>
              <div class="text-h4">{{ result.diffCount }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="3" class="d-flex">
          <v-card variant="tonal" :color="result.diffCount === 0 ? 'success' : 'info'" class="flex-grow-1 d-flex align-center justify-center">
            <v-card-text class="text-center">
              <div class="text-h6">Matching</div>
              <div class="text-h4">{{ result.totalFields - result.diffCount }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="3" class="d-flex align-center justify-end">
          <v-switch
            v-model="showOnlyDiffs"
            label="Show only differences"
            hide-details
            density="compact"
            color="primary"
          />
        </v-col>
      </v-row>

      <!-- Comparison table -->
      <div class="table-scroll">
        <v-table density="compact" class="vin-compare-table elevation-1">
          <colgroup>
            <col style="width: 180px" />
            <col style="width: calc(50% - 90px)" />
            <col style="width: calc(50% - 90px)" />
          </colgroup>
          <thead>
            <tr>
              <th class="text-left">Field</th>
              <th class="text-left">A5 Value</th>
              <th class="text-left">A6 Value</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in filteredComparison"
              :key="row.field"
              :class="{ 'compare-diff-row': !row.match }"
            >
              <td class="font-weight-medium text-body-2 field-cell">{{ row.field }}</td>
              <td class="text-body-2 value-cell" :class="{ 'compare-diff-cell': !row.match && row.a5Value != null }">
                <div class="value-content" :title="formatValue(row.a5Value)">{{ formatValue(row.a5Value) }}</div>
              </td>
              <td class="text-body-2 value-cell" :class="{ 'compare-diff-cell': !row.match && row.a6Value != null }">
                <div class="value-content" :title="formatValue(row.a6Value)">{{ formatValue(row.a6Value) }}</div>
              </td>
            </tr>
            <tr v-if="filteredComparison.length === 0">
              <td colspan="3" class="text-center text-grey pa-4">
                {{ showOnlyDiffs ? 'No differences found — all fields match.' : 'No fields to compare.' }}
              </td>
            </tr>
          </tbody>
        </v-table>
      </div>

      <div class="text-caption text-grey mt-3">
        Generated: {{ result.generatedAt ?? '—' }}
      </div>
    </div>

    <div v-else class="d-flex flex-column align-center justify-center my-12 text-grey">
      <v-icon size="64" color="grey-lighten-1" class="mb-4">mdi-file-compare</v-icon>
      <div class="text-h6 text-grey-darken-1">Enter a VIN to compare A5 vs A6 data</div>
      <div class="text-body-2 mt-1">Field-by-field comparison of SRP objects from both systems</div>
    </div>
  </div>
</template>

<style scoped>
.table-scroll {
  max-width: 100%;
  overflow-x: auto;
}

.vin-compare-table {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  table-layout: fixed;
  width: 100%;
}

.field-cell {
  word-break: break-word;
}

.value-cell {
  padding: 4px 8px !important;
}

.value-content {
  max-height: 4.5em;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  cursor: default;
}

.compare-diff-row {
  background-color: rgba(255, 235, 59, 0.12) !important;
}

.compare-diff-cell {
  font-weight: 600;
  color: #e65100;
}
</style>
